import {
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectBulkDeactivatingFromState,
  selectSelectedVirtualCardListModels,
  selectUpdateableSelectedVirtualCardListModels,
} from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.selectors'
import BetaTag from 'src/components/BetaTag/BetaTag'
import CancelOrDeactivateVirtualCardsPopup from 'src/components/CancelOrDeactivateVirtualCardsPopup/CancelOrDeactivateVirtualCardsPopup'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import TestId from 'src/components/TestId/TestId'
import { useFlyoutDispatch } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { createSetIsBulkDeactivateVirtualCardsPopupOpen } from 'src/redux/actions/app.actions'
import { putBulkVirtualCardDeactivateAndCloseModal } from 'src/redux/actions/data/virtualCards.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

const BulkDeactivateVirtualCardsPopup: React.FC = () => {
  const selectedVirtualCards = useSelector(selectSelectedVirtualCardListModels)
  const updateableVirtualCards = useSelector(
    selectUpdateableSelectedVirtualCardListModels,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)
  const bulkDeactivating = useSelector(selectBulkDeactivatingFromState)
  const { analytics } = useAnalyticsContext()

  const dispatch = useFlyoutDispatch()

  const selectedCardTypesList = (
    selectedVirtualCards: VirtualCardListModel[],
  ): React.ReactElement[] => {
    let standardCount = 0
    let recurringCount = 0
    let standardGuestCount = 0
    let recurringGuestCount = 0
    const cardTypeArray = []

    selectedVirtualCards.map((vc) => {
      if (vc.recurs) {
        if (vc.direct) {
          recurringGuestCount++
        } else {
          recurringCount++
        }
      } else {
        if (vc.direct) {
          standardGuestCount++
        } else {
          standardCount++
        }
      }
    })

    if (standardCount > 0) {
      cardTypeArray.push(
        <li>
          <span
            key={'standard'}
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translateSingleString(locale, 'Standard (${num})', {
              num: standardCount.toString(),
            })}
          </span>
        </li>,
      )
    }
    if (recurringCount > 0) {
      cardTypeArray.push(
        <li>
          <span
            key={'recurring'}
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translateSingleString(locale, 'Recurring (${num})', {
              num: recurringCount.toString(),
            })}
          </span>
        </li>,
      )
    }
    if (standardGuestCount > 0) {
      cardTypeArray.push(
        <li>
          <span
            key={'standard_guest'}
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translateSingleString(locale, 'Guest (${num})', {
              num: standardGuestCount.toString(),
            })}
          </span>
        </li>,
      )
    }
    if (recurringGuestCount > 0) {
      cardTypeArray.push(
        <li>
          <span
            key={'recurring_guest'}
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translateSingleString(locale, 'Recurring Guest (${num})', {
              num: recurringGuestCount.toString(),
            })}
          </span>
        </li>,
      )
    }

    return cardTypeArray
  }

  const deactivateVirtualCards = (): void => {
    const deactivateableVirtualCardIds = updateableVirtualCards.map(
      (vc) => vc.id,
    )
    dispatch(
      putBulkVirtualCardDeactivateAndCloseModal(deactivateableVirtualCardIds),
    )
    analytics?.track('deactivate card modal - deactivate', {
      bulk: true,
      number_of_cards: updateableVirtualCards.length,
    })
  }

  return (
    <>
      {updateableVirtualCards !== undefined &&
      updateableVirtualCards.length > 0 ? (
        <>
          <CancelOrDeactivateVirtualCardsPopup
            title={
              <div
                css={css`
                  display: flex;
                  gap: 12px;
                  align-items: center;
                `}
              >
                <Trns source={'Bulk Deactivate Virtual Cards'} />
                <BetaTag
                  css={css`
                    z-index: 100;
                  `}
                />
              </div>
            }
            subtitle={
              <>
                <div
                  css={css`
                    margin-bottom: 4px;
                  `}
                >
                  <Trns source={'Cards selected:'} />
                </div>
                <ul
                  css={css`
                    margin-left: 30px;
                    margin-bottom: 20px;
                  `}
                >
                  {selectedCardTypesList(updateableVirtualCards)}
                </ul>
                {!(
                  updateableVirtualCards.length === selectedVirtualCards.length
                ) && (
                  <Trns
                    source={
                      'Note that your card selection was truncated to include only cards that you are able to deactivate. This applies to cards you have created or have access to as a budget user.'
                    }
                  />
                )}
              </>
            }
            actionButtonLabel={
              <TestId testId={'bulkDeactivateConfirmButton'}>
                <Trns source={'Deactivate'} />
              </TestId>
            }
            actionButtonLoading={bulkDeactivating}
            actionButtonOnClick={deactivateVirtualCards}
            cancelButtonLabel={<Trns source={'Cancel'} />}
            cancelButtonOnClick={(): void => {
              dispatch(createSetIsBulkDeactivateVirtualCardsPopupOpen(false))
              analytics?.track('deactivate card modal - cancel', {
                bulk: true,
                number_of_cards: updateableVirtualCards.length,
              })
            }}
          />
        </>
      ) : (
        <Popup
          onClickClose={(): void => {
            dispatch(createSetIsBulkDeactivateVirtualCardsPopupOpen(false))
          }}
          showCloseButton
        >
          <NotFoundPopupContent />
        </Popup>
      )}
    </>
  )
}

export default BulkDeactivateVirtualCardsPopup
